import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import AsyncTable, { Column } from '../../../../components/AsyncTable';
import { ActionButtonsContainer } from '../../../../components/TablePaginator/styles';
import { DeleteIconTable, EditIconTable } from '../../../../components/TablePaginator/styles';
import NewRegisterBtn from '../../../../components/Buttons/NewRegisterBtn';
import { dbMapApi } from '../../../../services/requests';
import ContentContainer from '../../../../templates/Content/ContentContainer';
import { getGenericRequestPageForAsyncTable } from '../../../../utils/TablesFunctions';
import CreateDbMapModal from './components/CreateDbMapModal';
import EditDbMapModal from './components/EditDbMapModal';
import DeleteDbMapModal from './components/DeleteDbMapModal';
import checkPermission from '../../../../utils/checkPermission';

export type DbMapItem = {
  table: string;
  column: string;
  reference: string;
  created_at?: string;
  updated_at?: string;
};

const DbMapPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [tableData, setTableData] = useState<DbMapItem[]>([]);
  const [updateTable, setUpdateTable] = useState(0);

  const [selectedItem, setSelectedItem] = useState<DbMapItem | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const refreshTable = () => {
    setUpdateTable((prev) => prev + 1);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setSelectedItem(null);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedItem(null);
  };

  const openEditModal = (row: DbMapItem) => {
    setSelectedItem(row);
    setShowEditModal(true);
  };

  const openDeleteModal = (row: DbMapItem) => {
    setSelectedItem(row);
    setShowDeleteModal(true);
  };

  const actionsButtons = (row: DbMapItem) => (
    <ActionButtonsContainer type={2}>
      {checkPermission('dbMap-edit') && (
        <EditIconTable title={t('edit')} onClick={() => openEditModal(row)} />
      )}
      {checkPermission('dbMap-delete') && (
        <DeleteIconTable title={t('delete')} onClick={() => openDeleteModal(row)} />
      )}
    </ActionButtonsContainer>
  );

  const columns: Column<DbMapItem>[] = [
    {
      label: t('table') as string,
      accessor: 'table',
    },
    {
      label: t('column') as string,
      accessor: 'column',
    },
    {
      label: t('reference') as string,
      accessor: 'reference',
    },
    {
      label: t('actions') as string,
      Cell: actionsButtons,
    },
  ];

  return (
    <ContentContainer title={t('dbMap')}>
      {showCreateModal && (
        <CreateDbMapModal
          showModal={showCreateModal}
          closeModal={closeCreateModal}
          onSuccess={refreshTable}
        />
      )}

      {showEditModal && selectedItem && (
        <EditDbMapModal
          showModal={showEditModal}
          closeModal={closeEditModal}
          item={selectedItem}
          onSuccess={refreshTable}
        />
      )}

      {showDeleteModal && selectedItem && (
        <DeleteDbMapModal
          showModal={showDeleteModal}
          closeModal={closeDeleteModal}
          item={selectedItem}
          onSuccess={refreshTable}
        />
      )}

      {checkPermission('dbMap-create') && (
        <NewRegisterBtn
          onClick={() => setShowCreateModal(true)}
          style={{ marginLeft: '0', marginBottom: '1rem' }}
        />
      )}

      <AsyncTable
        tableName={t('dbMap')}
        columns={columns}
        value={tableData}
        onChange={setTableData}
        requestPage={getGenericRequestPageForAsyncTable(dbMapApi)}
        reqListener={[updateTable]}
        options={{
          styles: {
            primaryColor: `${theme.colors.surface}`,
            secondaryColor: `${theme.colors.onSurface}`,
            alternateRowColor: theme.colors.onSurfaceLight,
            textColor: theme.colors.text,
          },
          quantityPerPageLabel: t('quantityPerPage'),
          searchPlaceholder: t('search'),
        }}
      />
    </ContentContainer>
  );
};

export default DbMapPage;
